import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, ArrowRight, BadgeCheck, Calendar, ChevronDown, Clock, Download, Globe, MapPin,
  Phone, ShieldCheck, Star, Sun, Wallet, XCircle, FileText,
} from "lucide-react";
import { MONTHS } from "@/lib/months";

/** "Mar, Apr, May" style compact month list */
const formatMonths = (months) => months.map((m) => MONTHS[m - 1]).filter(Boolean).join(", ");
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { API, api, formatINR } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";

const categoryLabels = { domestic: "Domestic Tour", international: "International Tour", visa: "Visa Service" };

const PolicyBlock = ({ icon: Icon, title, items, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-cream/10 rounded-2xl overflow-hidden" data-testid={testid}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-cream/5 transition-colors"
        data-testid={`${testid}-toggle`}
      >
        <span className="flex items-center gap-3 text-cream text-sm font-semibold">
          <Icon size={17} className="text-gold" /> {title}
        </span>
        <ChevronDown size={17} className={`text-gold transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul className="px-6 pb-6 space-y-2.5">
          {items.map((item) => (
            <li key={item} className="text-sm text-mist flex items-start gap-2.5">
              <span className="text-gold mt-1 text-[10px]">◆</span> {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default function PackageDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const settings = useSettings();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    api
      .get(`/packages/${id}`)
      .then((res) => setPkg(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (notFound) {
    return (
      <div className="bg-ink min-h-screen text-cream">
        <Navbar />
        <div className="pt-40 pb-24 text-center px-4" data-testid="package-not-found">
          <h1 className="font-display text-3xl mb-4">This journey doesn't exist.</h1>
          <Link to="/" className="text-gold text-sm underline underline-offset-4">Return home</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-ink min-h-screen text-cream" data-testid="package-detail-page">
      <Navbar />

      {loading || !pkg ? (
        <div className="pt-32 pb-24 max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <div className="h-[46vh] rounded-3xl bg-card/60 animate-pulse mb-10" />
          <div className="h-40 rounded-2xl bg-card/60 animate-pulse" />
        </div>
      ) : (
        <>
          <section className="relative h-[56vh] min-h-[420px] overflow-hidden">
            <img src={pkg.image} alt={pkg.title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/45 to-ink" />
            <div className="relative z-10 h-full max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 flex flex-col justify-end pb-12">
              <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                <Link
                  to={`/packages/${pkg.category}`}
                  data-testid="detail-back-link"
                  className="inline-flex items-center gap-1.5 text-xs text-mist hover:text-gold transition-colors mb-5"
                >
                  <ArrowLeft size={14} /> {categoryLabels[pkg.category]}s
                </Link>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="bg-ink/80 backdrop-blur-sm text-gold text-[11px] font-mono tracking-widest uppercase px-3.5 py-1.5 rounded-full border border-gold/30">
                    {pkg.tag}
                  </span>
                  <span className="flex items-center gap-1 bg-ink/80 backdrop-blur-sm text-cream text-xs px-3.5 py-1.5 rounded-full border border-cream/15">
                    <Star size={12} className="text-gold fill-gold" /> {pkg.rating}
                  </span>
                </div>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-cream max-w-3xl" data-testid="detail-title">
                  {pkg.title}
                </h1>
                <div className="mt-4 flex flex-wrap gap-x-7 gap-y-2 text-sm text-mist">
                  <span className="flex items-center gap-2"><MapPin size={15} className="text-gold" /> {pkg.destination}</span>
                  <span className="flex items-center gap-2"><Clock size={15} className="text-gold" /> {pkg.duration}</span>
                  {pkg.tour_type && (
                    <span className="flex items-center gap-2" data-testid="detail-tour-type">
                      <BadgeCheck size={15} className="text-gold" /> {pkg.tour_type}
                    </span>
                  )}
                  {pkg.category !== "visa" && (pkg.countries?.length || pkg.states?.length) > 0 && (
                    <span className="flex items-center gap-2" data-testid="detail-region">
                      <Globe size={15} className="text-gold" />
                      {pkg.category === "domestic" ? pkg.states?.join(", ") : (pkg.countries?.join(", ") + (pkg.continent ? ` · ${pkg.continent}` : ""))}
                    </span>
                  )}
                </div>
              </motion.div>
            </div>
          </section>

          <section className="py-16 md:py-20 px-4 sm:px-8 lg:px-16">
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
              <div className="lg:col-span-8 space-y-14">
                {pkg.overview && (
                  <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
                    <p className="font-mono text-xs tracking-[0.35em] uppercase text-gold mb-4">Overview</p>
                    <p className="text-base sm:text-lg text-mist leading-relaxed" data-testid="detail-overview">{pkg.overview}</p>
                  </motion.div>
                )}

                {pkg.highlights?.length > 0 && (
                  <div data-testid="detail-highlights">
                    <p className="font-mono text-xs tracking-[0.35em] uppercase text-gold mb-6">
                      {pkg.category === "visa" ? "Service Highlights" : "Trip Highlights"}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {pkg.highlights.map((h, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: i * 0.06 }}
                          className="rounded-2xl border border-gold/20 bg-card/50 px-5 py-4 flex items-start gap-3"
                          data-testid={`highlight-${i}`}
                        >
                          <Star size={15} className="text-gold shrink-0 mt-0.5" />
                          <span className="text-sm text-mist leading-relaxed">{h}</span>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}

                {pkg.themes?.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2.5" data-testid="detail-themes">
                    <span className="text-[11px] font-mono tracking-widest uppercase text-faded mr-1">Themes:</span>
                    {pkg.themes.map((t) => (
                      <span key={t} className="text-xs text-gold bg-gold/10 border border-gold/25 px-3.5 py-1.5 rounded-full">
                        {t}
                      </span>
                    ))}
                  </div>
                )}

                {pkg.category === "visa" && (
                  <div className="space-y-10">
                    {pkg.documents?.length > 0 && (
                      <div className="rounded-2xl border border-gold/20 bg-card/50 p-7" data-testid="detail-visa-documents">
                        <p className="flex items-center gap-2 font-mono text-xs tracking-[0.3em] uppercase text-gold mb-5">
                          <FileText size={16} /> Documents Required
                        </p>
                        <ul className="space-y-3">
                          {pkg.documents.map((doc, i) => (
                            <li key={i} className="text-sm text-mist flex items-start gap-2.5">
                              <BadgeCheck size={15} className="text-gold shrink-0 mt-0.5" /> {doc}
                            </li>
                          ))}
                        </ul>
                        <p className="text-[11px] text-faded mt-5">Document requirements vary by embassy and traveller profile — your consultant shares a personalised checklist after the first review.</p>
                      </div>
                    )}
                  </div>
                )}

                {pkg.itinerary?.length > 0 && (
                  <div data-testid="detail-itinerary">
                    <p className="font-mono text-xs tracking-[0.35em] uppercase text-gold mb-8">
                      {pkg.category === "visa" ? "The Process" : "Day-wise Itinerary"}
                    </p>
                    <div className="space-y-0">
                      {pkg.itinerary.map((step, i) => (
                        <motion.div
                          key={step.day}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true, margin: "-40px" }}
                          transition={{ duration: 0.5, delay: i * 0.05 }}
                          className="relative flex gap-6 pb-8 last:pb-0"
                        >
                          {i < pkg.itinerary.length - 1 && (
                            <span className="absolute left-[21px] top-12 bottom-0 w-px bg-gold/20" />
                          )}
                          <span className="shrink-0 w-11 h-11 rounded-full border border-gold/40 bg-card flex items-center justify-center font-mono text-xs text-gold">
                            {String(step.day).padStart(2, "0")}
                          </span>
                          <div className="pt-2">
                            <h3 className="font-display text-xl text-cream mb-1">{step.title}</h3>
                            <p className="text-sm text-mist leading-relaxed">{step.description}</p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}

                {pkg.departures?.length > 0 && (
                  <div data-testid="detail-departures">
                    <p className="font-mono text-xs tracking-[0.35em] uppercase text-gold mb-6">Fixed Departures</p>
                    <div className="space-y-3">
                      {pkg.departures.map((d, i) => {
                        const left = Number(d.seats_left ?? 0);
                        const soldOut = left <= 0;
                        const filling = !soldOut && left <= 6;
                        const dateLabel = new Date(`${d.date}T00:00:00`).toLocaleDateString("en-IN", {
                          weekday: "short", day: "numeric", month: "short", year: "numeric",
                        });
                        return (
                          <div
                            key={i}
                            data-testid={`departure-row-${i}`}
                            className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-2xl border px-5 py-4 ${
                              soldOut ? "border-cream/10 opacity-50" : "border-gold/20 bg-card/50"
                            }`}
                          >
                            <div>
                              <p className="text-sm font-semibold text-cream flex items-center gap-2">
                                <Calendar size={14} className="text-gold" /> {dateLabel}
                              </p>
                              <p className="text-[11px] font-mono tracking-widest uppercase text-faded mt-1">
                                {d.seats_total} seats per departure
                              </p>
                            </div>
                            <div className="flex items-center gap-3">
                              <span
                                data-testid={`departure-seats-${i}`}
                                className={`text-[11px] font-mono tracking-widest uppercase px-3 py-1.5 rounded-full border ${
                                  soldOut
                                    ? "text-faded border-cream/20"
                                    : filling
                                    ? "text-amber-300 border-amber-400/40 bg-amber-400/10"
                                    : "text-emerald-300 border-emerald-400/30 bg-emerald-400/10"
                                }`}
                              >
                                {soldOut ? "Sold out" : filling ? `Only ${left} left` : `${left} seats left`}
                              </span>
                              {!soldOut && (
                                <button
                                  onClick={() => navigate("/", { state: { scrollTo: "enquiry", service: pkg.category, travelDate: d.date, packageId: pkg.id, packageTitle: pkg.title } })}
                                  data-testid={`departure-enquire-${i}`}
                                  className="text-xs font-semibold text-ink bg-gold px-4 py-2 rounded-full hover:bg-gold-hover transition-colors"
                                >
                                  Enquire
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {pkg.inclusions?.length > 0 && (
                    <div className="rounded-2xl border border-gold/20 bg-card/50 p-7" data-testid="detail-inclusions">
                      <p className="flex items-center gap-2 font-mono text-xs tracking-[0.3em] uppercase text-gold mb-5">
                        <BadgeCheck size={16} /> Inclusive
                      </p>
                      <ul className="space-y-3">
                        {pkg.inclusions.map((item) => (
                          <li key={item} className="text-sm text-mist flex items-start gap-2.5">
                            <BadgeCheck size={15} className="text-gold shrink-0 mt-0.5" /> {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {pkg.exclusions?.length > 0 && (
                    <div className="rounded-2xl border border-red-400/25 bg-card/50 p-7" data-testid="detail-exclusions">
                      <p className="flex items-center gap-2 font-mono text-xs tracking-[0.3em] uppercase text-red-400 mb-5">
                        <XCircle size={16} /> Exclusive
                      </p>
                      <ul className="space-y-3">
                        {pkg.exclusions.map((item) => (
                          <li key={item} className="text-sm text-mist flex items-start gap-2.5">
                            <XCircle size={15} className="text-red-400 shrink-0 mt-0.5" /> {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {pkg.category !== "visa" && pkg.price_tiers && (
                  <div data-testid="detail-price-tiers">
                    <p className="font-mono text-xs tracking-[0.35em] uppercase text-gold mb-6">Tour Price — Per Person</p>
                    <div className="rounded-2xl border border-gold/20 overflow-hidden">
                      {[
                        ["Adult — Single Sharing", pkg.price_tiers.single, "tier-single"],
                        ["Adult — Double Sharing", pkg.price_tiers.double, "tier-double"],
                        ["Adult — Triple Sharing", pkg.price_tiers.triple, "tier-triple"],
                        ["Child With Bed (CWB)", pkg.price_tiers.cwb, "tier-cwb"],
                        ["Child No Bed (CWNB)", pkg.price_tiers.cwnb, "tier-cwnb"],
                        ["Infant", pkg.price_tiers.infant, "tier-infant"],
                      ].map(([label, val, tid], i) => (
                        <div
                          key={tid}
                          data-testid={`price-${tid}`}
                          className={`flex items-center justify-between px-6 py-4 ${i % 2 === 0 ? "bg-card/60" : "bg-surface/40"}`}
                        >
                          <span className="text-sm text-mist">{label}</span>
                          <span className="font-display text-lg text-gold">{val ? formatINR(val) : "On request"}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-faded mt-3">Prices are indicative per person; final cost depends on dates, hotel category and availability.</p>
                  </div>
                )}

                {pkg.category === "visa" && (pkg.visa_fee > 0 || pkg.processing_time || pkg.visa_type) && (
                  <div data-testid="detail-visa-facts" className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {pkg.visa_fee > 0 && (
                      <div className="rounded-2xl border border-gold/20 bg-card/50 p-5">
                        <p className="text-[11px] font-mono tracking-widest uppercase text-faded mb-1.5">Visa Fee</p>
                        <p className="font-display text-2xl text-gold">{formatINR(pkg.visa_fee)}</p>
                        <p className="text-[11px] text-faded mt-1">per person, indicative</p>
                      </div>
                    )}
                    {pkg.processing_time && (
                      <div className="rounded-2xl border border-gold/20 bg-card/50 p-5">
                        <p className="text-[11px] font-mono tracking-widest uppercase text-faded mb-1.5">Processing Time</p>
                        <p className="font-display text-2xl text-cream">{pkg.processing_time}</p>
                        <p className="text-[11px] text-faded mt-1">indicative</p>
                      </div>
                    )}
                    {pkg.visa_type && (
                      <div className="rounded-2xl border border-gold/20 bg-card/50 p-5">
                        <p className="text-[11px] font-mono tracking-widest uppercase text-faded mb-1.5">Visa Type</p>
                        <p className="font-display text-2xl text-cream">{pkg.visa_type}</p>
                        <p className="text-[11px] text-faded mt-1">as filed with the embassy</p>
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-4">
                  {pkg.terms?.length > 0 && <PolicyBlock icon={FileText} title="Terms & Conditions" items={pkg.terms} testid="detail-terms" />}
                  {pkg.payment_policy?.length > 0 && <PolicyBlock icon={Wallet} title="Payment Policy" items={pkg.payment_policy} testid="detail-payment" />}
                  {pkg.cancellation_policy?.length > 0 && <PolicyBlock icon={ShieldCheck} title="Cancellation Policy" items={pkg.cancellation_policy} testid="detail-cancellation" />}
                </div>
              </div>

              <div className="lg:col-span-4">
                <div className="lg:sticky lg:top-28 rounded-3xl border border-gold/25 bg-card/70 p-8" data-testid="detail-booking-card">
                  <p className="text-[11px] uppercase tracking-widest text-faded">
                    {pkg.category === "visa" ? "Service fee" : "Starting from"}
                  </p>
                  <p className="font-display text-4xl text-gold mt-1 mb-1" data-testid="detail-price">{formatINR(pkg.price)}</p>
                  <p className="text-xs text-faded mb-7">{pkg.category === "visa" ? "per application" : "per person, twin sharing"}</p>

                  <div className="space-y-3 mb-8 text-sm text-mist">
                    <p className="flex items-center gap-2.5"><Clock size={15} className="text-gold" /> {pkg.duration}</p>
                    <p className="flex items-center gap-2.5"><MapPin size={15} className="text-gold" /> {pkg.destination}</p>
                    {pkg.category !== "visa" && (
                      <p className="flex items-center gap-2.5"><Calendar size={15} className="text-gold" /> Flexible departures</p>
                    )}
                    {pkg.best_months?.length > 0 && pkg.best_months.length < 12 && (
                      <p className="flex items-center gap-2.5" data-testid="detail-best-months">
                        <Sun size={15} className="text-gold" /> Best months: {formatMonths(pkg.best_months)}
                      </p>
                    )}
                  </div>

                  <button
                    onClick={() => navigate("/", { state: { scrollTo: "enquiry", service: pkg.category } })}
                    data-testid="detail-enquire-btn"
                    className="group w-full flex items-center justify-center gap-2 bg-gold text-ink font-semibold text-sm px-8 py-4 rounded-full hover:bg-gold-hover transition-colors mb-3"
                  >
                    Enquire About This {pkg.category === "visa" ? "Service" : "Trip"}
                    <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                  <a
                    href={`tel:${settings.phone.replace(/\s/g, "")}`}
                    data-testid="detail-call-btn"
                    className="w-full flex items-center justify-center gap-2 border border-cream/25 text-cream text-sm px-8 py-4 rounded-full hover:border-gold hover:text-gold transition-colors"
                  >
                    <Phone size={15} /> {settings.phone}
                  </a>
                  <a
                    href={`${API}/packages/${pkg.id}/pdf`}
                    data-testid="detail-download-btn"
                    className="w-full flex items-center justify-center gap-2 text-xs text-mist hover:text-gold transition-colors mt-4"
                  >
                    <Download size={14} /> Download {pkg.category === "visa" ? "visa guide" : "full package"} PDF
                  </a>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
